import type { ReactNode } from 'react'
import { IconTile, type IconTileGradient } from '../ui/icon-tile'

import { cn } from '../../lib/utils'

export type PageHeaderProps = {
    /**
     * MDI path from `@mdi/js` — the app's icon library
     * (`docs/frontend/icons.md`), not lucide. Take it from
     * `lib/section-icons.ts` so the header icon is the sidebar icon of the
     * page's section rather than a second opinion.
     */
    icon: string
    /**
     * Which gradient the icon tile carries. `brand` on every page today — the
     * prop exists so a page whose subject has its own identity colour (an
     * asset type, a KPI family) can say so without hand-rolling a tile. Spec
     * 2026-09-17-02's rule holds: at most ONE `lg` tile per view, and on a
     * routed page that tile is this one.
     */
    gradient?: IconTileGradient
    /**
     * Plain string for list / create / settings pages; a ReactNode for detail
     * pages whose H1 is click-to-edit (patterns §1). Whatever is passed lands
     * inside THE `<h1>` — callers never render one of their own.
     */
    title: ReactNode
    /** Sub-title line under the title. Already translated. */
    description?: ReactNode
    /** Chips on the title row: an external-id code, a status pill, a badge. */
    titleAside?: ReactNode
    /**
     * Right-aligned action bar. Buttons follow patterns §5 — labelled buttons
     * collapse to icon-only below `md`, Edit / Delete are always icon-only.
     */
    actions?: ReactNode
    className?: string
    /**
     * Opt-in: lets the title WRAP instead of truncating. Default is
     * unchanged for every other page — the title truncates so a long one
     * never pushes the action bar off the row (there is no action bar to
     * protect here in the one case that needs this: a public resident page,
     * spec 2026-09-25-05, where the title is the resident's own subject line
     * and truncating it would hide their own words).
     */
    wrap?: boolean
    /**
     * Lands on the `<h1>`, not on the wrapper: it replaces the test id the
     * migrated page used to put on its own heading, so `getByTestId(...)`
     * keeps resolving to the title text and nothing else.
     */
    'data-testid'?: string
}

/**
 * THE page header. One component, one `<h1>`, one type scale.
 *
 * Before this existed every page hand-rolled its own title row and the app
 * had 25 distinct `<h1 className>` strings for what is supposed to be one
 * thing — different sizes, different weights, the primary action sometimes on
 * the title row and sometimes under it, an icon on a handful of pages and not
 * on the rest. Centralising it means the scale is a one-line change and drift
 * is impossible by construction; `page-headers.test.ts` is the ratchet that
 * keeps a new page from re-introducing a raw `<h1>`.
 *
 * `app-shell-headings.test.ts` guarantees the chrome contributes no `<h1>`;
 * this component is where the page's single one lives from now on.
 *
 * Layout: icon square, then a `min-w-0` title block that TRUNCATES rather than
 * pushing the actions off the row, then the action bar. Below `md` the title
 * block takes the full row and the actions wrap underneath it, so a long title
 * never squeezes the buttons into a column of icons on a phone.
 */
export function PageHeader({
    icon,
    gradient = 'brand',
    title,
    description,
    titleAside,
    actions,
    className,
    wrap = false,
    'data-testid': dataTestId,
}: PageHeaderProps) {
    return (
        <div className={cn('flex flex-wrap items-start gap-x-3 gap-y-3', className)}>
            <div className="flex min-w-0 flex-1 basis-full items-start gap-3 md:basis-0">
                <IconTile path={icon} gradient={gradient} size="lg" />
                <div className="min-w-0 flex-1">
                    {/* `basis-48` on the h1 is what makes the chips WRAP under
                        a squeezed title instead of eating it: with a zero
                        basis the row always fits and the truncated title
                        collapses to nothing on a phone. */}
                    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                        <h1
                            className={cn(
                                'min-w-0 flex-1 basis-48 text-xl font-semibold tracking-tight text-foreground sm:text-2xl',
                                wrap ? 'whitespace-normal break-words' : 'truncate',
                            )}
                            data-testid={dataTestId}
                        >
                            {title}
                        </h1>
                        {titleAside ? (
                            <div className="flex shrink-0 items-center gap-2">{titleAside}</div>
                        ) : null}
                    </div>
                    {description ? (
                        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
                    ) : null}
                </div>
            </div>
            {actions ? (
                <div className="ml-auto flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
            ) : null}
        </div>
    )
}
